import { Box, styled } from '@mui/material';
import type { Control, FieldPath, FieldValues } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';

import PhoneInput from '@/components/forms/PhoneInput';
import { emptyStringIfOnlyNanpCallingCode } from '@/services/phoneUtils';

import type { ReservationFormInput } from '../schema/reservationForm.schema';
import { useReservationFormMachines } from '../ReservationFormMachinesContext';
import { useFieldActorSnapshot } from '../hooks/useFieldActorSnapshot';

const PREFIX = 'PhoneInputField';

const classes = {
  container: `${PREFIX}-container`,
  extension: `${PREFIX}-extension`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    [theme.breakpoints.down('md')]: {
      width: '100%',
      '& .MuiFormControl-root': {
        width: '100%',
      },
    },
    [theme.breakpoints.up('md')]: {
      width: '50%',
    },
  },
  [`& .${classes.extension}`]: {
    [theme.breakpoints.down('md')]: {
      width: `calc(100% - ${theme.spacing(4)})`,
    },
  },
}));

type PhoneInputFieldBase = {
  label: string;
  required?: boolean;
  className?: string;
};

type TripLegPhoneInputProps = PhoneInputFieldBase & {
  index: number;
  tripField: 'pickupPhone' | 'dropoffPhone';
  extensionField: 'pickupPhoneExtension' | 'dropoffPhoneExtension';
};

type MemberPhoneInputProps = PhoneInputFieldBase & {
  tripField:
    | 'MemberHomePhone'
    | 'MemberMobilePhone'
    | 'AlternateContactPhone';
};

export type PhoneInputFieldProps =
  | TripLegPhoneInputProps
  | MemberPhoneInputProps;

function isMemberPhoneField(
  props: PhoneInputFieldProps,
): props is MemberPhoneInputProps {
  return (
    props.tripField === 'MemberHomePhone' ||
    props.tripField === 'MemberMobilePhone' ||
    props.tripField === 'AlternateContactPhone'
  );
}

export function PhoneInputField(props: PhoneInputFieldProps) {
  const { label, required, className } = props;
  const isMemberField = isMemberPhoneField(props);

  const { control, formState, setValue, getFieldState, getValues } =
    useFormContext<ReservationFormInput>();
  const { fieldRefs } = useReservationFormMachines();

  const phoneName: FieldPath<ReservationFormInput> = isMemberField
    ? props.tripField
    : (`trips.${props.index}.${props.tripField}` as const);
  const extensionName = isMemberField
    ? undefined
    : (`trips.${props.index}.${props.extensionField}` as const);

  const actor = isMemberField ? null : fieldRefs[props.tripField];
  const tripIndex = isMemberField ? null : props.index;
  useFieldActorSnapshot(actor);

  const phoneInput = (
    <PhoneInput
      label={label}
      name={phoneName}
      extensionName={extensionName}
      control={control as unknown as Control<FieldValues>}
      errors={formState.errors}
      required={required}
      className={className}
      disableDropdown={true}
      onValueChange={(value) => {
        const normalized = emptyStringIfOnlyNanpCallingCode(value);
        if (tripIndex !== null) {
          actor?.send({
            type: 'CHANGE',
            index: tripIndex,
            value: normalized,
          });
        }
        const hadError = Boolean(getFieldState(phoneName).error);
        setValue(phoneName, normalized, {
          shouldDirty: true,
          shouldValidate: hadError,
        });
      }}
      onBlur={() => {
        if (tripIndex !== null) {
          actor?.send({ type: 'BLUR', index: tripIndex });
        }
        const normalized = emptyStringIfOnlyNanpCallingCode(
          String(getValues(phoneName) ?? ''),
        );
        if (tripIndex !== null) {
          actor?.send({ type: 'CHANGE', index: tripIndex, value: normalized });
        }
        if (getValues(phoneName) !== normalized) {
          setValue(phoneName, normalized, { shouldDirty: true });
        }
      }}
    />
  );

  if (isMemberField) {
    return phoneInput;
  }

  return <StyledBox className={classes.container}>{phoneInput}</StyledBox>;
}
